import { useEffect, useRef, useState, type DragEvent } from "react";
import type { LocationNode } from "@iltocat/shared";
import { DRAG_TYPE, LOCATION_DRAG_TYPE, readDrag, subtreeIds, type TreeMatch } from "../lib/locationMoves.js";
import { Icon } from "./Icon.js";

/** How long a drag has to hover over a closed location before it opens. */
const OPEN_ON_HOVER_MS = 650;

/**
 * The whole location tree, folded: every place at once, opened only as far as
 * somebody has opened it. The shelf view shows one node at a time because that
 * is how you walk a basement; this is how you *aim* at one, which the sidebar
 * on the Locations screen and the Move dialog both need.
 *
 * With `onDropItems`, each row also takes a drag of items (see `DRAG_TYPE`).
 * With `onDropLocation`, each row can itself be dragged onto another, to move
 * that location inside it -- never into itself or anything below it, which
 * the rows under the pointer refuse. Hovering a closed row with either kind of
 * drag opens it after a moment, so a bin three levels down is reachable
 * without letting go.
 *
 * `unfiled` adds a row above the tree for "no location", which is null
 * everywhere it is passed around. It takes items, not locations: "Not filed"
 * is not a place a shelf can go.
 */
export function LocationTree({
  tree,
  current,
  here,
  hidden,
  open,
  onToggle,
  onPick,
  match,
  unfiled,
  onDropItems,
  onDropLocation,
}: {
  tree: readonly LocationNode[];
  /** Drawn as chosen: the location on screen, or null for the unfiled row. */
  current?: string | null;
  /** Where the things being moved already are, marked so it is not picked by mistake. */
  here?: string | null | undefined;
  /** Rows not to draw at all, with everything below them: a location being moved, when choosing where to. */
  hidden?: ReadonlySet<string>;
  open: ReadonlySet<string>;
  onToggle: (id: string, open: boolean) => void;
  onPick: (id: string | null) => void;
  /** A search in progress: only these rows, opened to show the hits. */
  match?: TreeMatch | null;
  unfiled?: { label: string; count?: number };
  onDropItems?: (to: string | null, ids: string[]) => void;
  onDropLocation?: (moving: string, into: string) => void;
}) {
  const [over, setOver] = useState<string | null | undefined>(undefined);
  const timer = useRef<number | undefined>(undefined);
  // The branch being dragged, which a drag-over cannot read out of the event:
  // browsers hide a drag's data until the drop.
  const moving = useRef<ReadonlySet<string> | null>(null);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const leave = () => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
    setOver(undefined);
  };

  /** Whether this row takes what is being dragged over it. */
  const takes = (event: DragEvent, id: string | null): boolean => {
    const types = event.dataTransfer.types;
    if (onDropItems && types.includes(DRAG_TYPE)) return true;
    if (onDropLocation && types.includes(LOCATION_DRAG_TYPE)) {
      return id !== null && !(moving.current?.has(id) ?? true);
    }
    return false;
  };

  /** Drag handlers for one row, or nothing when this tree takes no drops. */
  const dropProps = (id: string | null, closedWithChildren: boolean) =>
    onDropItems || onDropLocation
      ? {
          onDragOver: (event: DragEvent) => {
            if (!takes(event, id)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            if (over === id) return;
            window.clearTimeout(timer.current);
            setOver(id);
            if (id && closedWithChildren) {
              timer.current = window.setTimeout(() => onToggle(id, true), OPEN_ON_HOVER_MS);
            }
          },
          onDragLeave: (event: DragEvent) => {
            // Moving onto the row's own count or icon fires a leave; only
            // leaving the row itself counts.
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) leave();
          },
          onDrop: (event: DragEvent) => {
            leave();
            if (!takes(event, id)) return;
            const place = event.dataTransfer.getData(LOCATION_DRAG_TYPE);
            if (place && id !== null) {
              event.preventDefault();
              onDropLocation?.(place, id);
              return;
            }
            const ids = readDrag(event.dataTransfer.getData(DRAG_TYPE));
            if (!ids) return;
            event.preventDefault();
            onDropItems?.(id, ids);
          },
        }
      : {};

  /** A row that can itself be picked up, when locations can be moved by dragging. */
  const dragProps = (node: LocationNode) =>
    onDropLocation
      ? {
          draggable: true,
          onDragStart: (event: DragEvent) => {
            event.stopPropagation();
            moving.current = subtreeIds(node);
            event.dataTransfer.setData(LOCATION_DRAG_TYPE, node.id);
            event.dataTransfer.effectAllowed = "move";
          },
          onDragEnd: () => {
            moving.current = null;
            leave();
          },
        }
      : {};

  const rowClass = (id: string | null) =>
    ["tree-row", current === id && "tree-row-on", over === id && "tree-row-drop"].filter(Boolean).join(" ");

  const shown = (node: LocationNode) => !hidden?.has(node.id) && (!match || match.visible.has(node.id));

  const renderNode = (node: LocationNode, depth: number) => {
    if (!shown(node)) return null;
    const hasChildren = node.children.some(shown);
    const isOpen = hasChildren && (open.has(node.id) || Boolean(match?.open.has(node.id)));
    const isHit = Boolean(match?.hits.includes(node.id));
    return (
      <li key={node.id}>
        <div
          className={rowClass(node.id)}
          style={{ paddingInlineStart: `calc(${depth} * var(--space-4))` }}
          {...dropProps(node.id, hasChildren && !isOpen)}
          {...dragProps(node)}
        >
          {hasChildren ? (
            <button
              type="button"
              className="tree-twist"
              aria-expanded={isOpen}
              aria-label={`${isOpen ? "Close" : "Open"} ${node.name}`}
              disabled={Boolean(match)}
              onClick={() => onToggle(node.id, !isOpen)}
            >
              <Icon name={isOpen ? "chevron-down" : "chevron-right"} size={14} />
            </button>
          ) : (
            <span className="tree-twist" aria-hidden="true" />
          )}
          <button
            type="button"
            className={isHit ? "tree-pick tree-hit" : "tree-pick"}
            aria-current={current === node.id ? "true" : undefined}
            onClick={() => onPick(node.id)}
          >
            <Icon name="folder" size={16} />
            <span className="tree-name">{node.name}</span>
            {here === node.id ? (
              <span className="tree-here">here now</span>
            ) : (
              node.totalCount > 0 && <span className="tree-count">{node.totalCount}</span>
            )}
          </button>
        </div>
        {isOpen && (
          <ul className="tree-list">{node.children.map((child) => renderNode(child, depth + 1))}</ul>
        )}
      </li>
    );
  };

  return (
    <ul className="tree-list tree-root">
      {unfiled && !match && (
        <li>
          <div className={rowClass(null)} {...dropProps(null, false)}>
            <span className="tree-twist" aria-hidden="true" />
            <button
              type="button"
              className="tree-pick"
              aria-current={current === null ? "true" : undefined}
              onClick={() => onPick(null)}
            >
              <Icon name="box" size={16} />
              <span className="tree-name">{unfiled.label}</span>
              {here === null ? (
                <span className="tree-here">here now</span>
              ) : (
                Boolean(unfiled.count) && <span className="tree-count">{unfiled.count}</span>
              )}
            </button>
          </div>
        </li>
      )}
      {tree.map((node) => renderNode(node, 0))}
    </ul>
  );
}
